import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { usePatient, usePatientVisits, useVisitPrescriptions, useVisitLabOrders, useInvoices, useDocuments, type VisitAggregate } from "@/lib/queries";
import { useDeletePatient } from "@/lib/mutations";
import { apiFetch } from "@/lib/api";
import { createPrescriptionPdf } from "@/lib/pdf/prescriptionPdf";
import { printPdf } from "@/lib/print";
import { printCompositionPref } from "@/lib/rxPrint";
import { toast } from "sonner";
import { Has } from "@/components/Has";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PatientFlagsPanel } from "@/features/patients/PatientFlagsPanel";
import { PatientPhotoCard } from "@/features/patients/PatientPhotoCard";
import { BookDialog } from "@/features/appointments/BookDialog";
import { UploadDialog } from "@/components/UploadDialog";
import { DocumentViewer } from "@/features/emr/DocumentViewer";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtINR } from "@emr/shared/india";
import { computeAgeYm, formatAgeLabel } from "@emr/shared/age";
import { CalendarPlus, Pencil, Trash, CloudUpload, FileText, Activity, Printer, LoaderCircle, type LucideIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { InvoiceStatusBadge } from "@/components/StatusBadge";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = usePatient(id);
  const [bookOpen, setBookOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const deleteMut = useDeletePatient();
  const { isAdmin, permissions } = useAuth();
  const canEdit = isAdmin || permissions.has("patients.edit");
  const canDelete = isAdmin || permissions.has("patients.delete");
  const canBook = isAdmin || permissions.has("appointments.create");
  const canUpload = isAdmin || permissions.has("documents.create");

  if (isLoading) return <div className="p-6"><Skeleton className="h-96" /></div>;
  const p = data?.patient;
  if (!p) return <div className="p-6 text-muted">Patient not found.</div>;

  const age = formatAgeLabel(computeAgeYm(p.dob)) || "—";

  const doDelete = async () => {
    try {
      await deleteMut.mutateAsync(p.id);
      navigate("/patients");
    } catch { /* handled */ }
  };

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Passport photo — shown when present, placeholder + Add button when empty. */}
          <PatientPhotoCard patientId={p.id} photoR2Key={p.photoR2Key} variant="compact" />
          <div>
            <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">{[p.salutation, p.name].filter(Boolean).join(" ")}</h1>
            <div className="text-sm text-muted">{p.clinicUhid} · {age} · {p.gender ?? "—"} · {p.phone ?? "no phone"}</div>
          </div>
        </div>
        <div className="flex gap-2">
          {canBook && <Button onClick={() => setBookOpen(true)} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><CalendarPlus size={16} className="mr-1" /> Book</Button>}
          {canEdit && <Button variant="outline" onClick={() => navigate(`/patients/${p.id}/edit`)}><Pencil size={16} className="mr-1" /> Edit</Button>}
          {canDelete && <Button variant="ghost" className="text-red-600" onClick={() => setDeleteOpen(true)}><Trash size={16} className="mr-1" /> Delete</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="visits">Visits</TabsTrigger>
          <TabsTrigger value="prescriptions">Prescriptions</TabsTrigger>
          <TabsTrigger value="labs">Labs</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="flags">Flags</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader><CardTitle className="text-maroon-700 dark:text-amber-500">Patient details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-y-2 text-sm md:grid-cols-3">
              <div><span className="text-muted">UHID: </span>{p.clinicUhid}</div>
              {p.oldUhid && <div><span className="text-muted">Old UHID: </span>{p.oldUhid}</div>}
              <div><span className="text-muted">DOB: </span>{p.dob ? fmtDate(p.dob) : "—"}</div>
              <div><span className="text-muted">Gender: </span>{p.gender ?? "—"}</div>
              <div><span className="text-muted">Phone: </span>{p.phone ?? "—"}</div>
              <div><span className="text-muted">Alt phone: </span>{p.altPhone ?? "—"}</div>
              <div><span className="text-muted">Email: </span>{p.email ?? "—"}</div>
              <div className="col-span-2"><span className="text-muted">Address: </span>{[p.address, p.state, p.pincode].filter(Boolean).join(", ") || "—"}</div>
              <div><span className="text-muted">Aadhaar ID: </span>{p.aadhaarId ?? "—"}</div>
              <div><span className="text-muted">ABHA: </span>{p.abhaId ?? "—"}</div>
              <div><span className="text-muted">Created: </span>{fmtDate(p.createdAt)}</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="visits">
          <PatientVisitsTab patientId={p.id} />
        </TabsContent>

        <TabsContent value="prescriptions">
          <PatientPrescriptionsTab patientId={p.id} />
        </TabsContent>

        <TabsContent value="labs">
          <PatientLabsTab patientId={p.id} />
        </TabsContent>

        <TabsContent value="invoices">
          <PatientInvoicesTab patientId={p.id} />
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-maroon-700 dark:text-amber-500">Documents</CardTitle>
              {canUpload && (
                <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
                  <CloudUpload size={14} className="mr-1" /> Upload
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <DocumentViewerInline patientId={p.id} onOpenExternal={() => setDocsOpen(true)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="flags">
          <Card>
            <CardHeader><CardTitle className="text-maroon-700 dark:text-amber-500">Flags</CardTitle></CardHeader>
            <CardContent><PatientFlagsPanel patientId={p.id} /></CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <BookDialog open={bookOpen} onOpenChange={setBookOpen} patientId={p.id} />
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} patientId={p.id} />
      <DocumentViewer open={docsOpen} onOpenChange={setDocsOpen} patientId={p.id} />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete patient?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted">
            This will permanently delete {[p.salutation, p.name].filter(Boolean).join(" ")} and their entire clinical history — visits, notes, prescriptions, lab orders, invoices, and documents. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleteMut.isPending}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PatientVisitsTab({ patientId }: { patientId: string }) {
  const { data, isLoading } = usePatientVisits(patientId);
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canViewEmr = isAdmin || permissions.has("emr.view");
  const rows = data?.rows ?? [];

  if (isLoading) return <Skeleton className="h-32" />;
  if (rows.length === 0) return <EmptyState icon={CalendarPlus} label="No visits yet" />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 dark:bg-dark-700">
              <TableHead>Date</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Diagnosis</TableHead>
              <TableHead>Review</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((v) => (
              <TableRow
                key={v.id}
                className="cursor-pointer even:bg-cream-50/50"
                onClick={() => {
                  if (canViewEmr) navigate(`/emr/visits/${v.id}`);
                }}
              >
                <TableCell className="text-sm">{fmtDate(v.checkInAt)}</TableCell>
                <TableCell className="text-sm">{v.doctorName ?? "—"}</TableCell>
                <TableCell className="max-w-xs truncate text-sm text-muted">{v.diagnosis ?? "—"}</TableCell>
                <TableCell className="text-xs">{v.reviewNeeded ? "If needed" : v.reviewDate ? fmtDate(v.reviewDate) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PatientPrescriptionsTab({ patientId }: { patientId: string }) {
  const { data: visitsData, isLoading: visitsLoading } = usePatientVisits(patientId);
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canViewEmr = isAdmin || permissions.has("emr.view");
  const visits = visitsData?.rows ?? [];
  // Fetch prescriptions for the hovered visit (shows the drug count).
  const [expanded, setExpanded] = useState<string | null>(null);
  const prescriptionQuery = useVisitPrescriptions(expanded ?? undefined);
  const prescriptions = prescriptionQuery.data?.rows ?? [];
  // Visit whose prescription PDF is currently being built (row-level spinner).
  const [printingVisitId, setPrintingVisitId] = useState<string | null>(null);

  if (visitsLoading) return <Skeleton className="h-32" />;
  if (visits.length === 0) return <EmptyState icon={FileText} label="No prescriptions yet" />;

  // Direct print of a past visit's prescription: fetch the visit aggregate
  // (visit + note + prescription medicines in one call), rebuild the same
  // PDF the EMR visit page prints, and open the browser print preview.
  // Gated on emr.view — GET /visits/:id requires it.
  const printPrescription = async (visitId: string) => {
    setPrintingVisitId(visitId);
    try {
      const agg = await apiFetch<VisitAggregate>(`/visits/${visitId}`);
      const v = agg.visit;
      const rx = agg.prescriptions[0] ?? null;
      if (!rx || rx.medicines.length === 0) {
        toast.error("No prescription to print");
        return;
      }
      const doc = await createPrescriptionPdf({
        patientName: v.patientName ?? "Patient",
        clinicUhid: v.clinicUhid ?? "",
        patientDob: v.patientDob,
        patientGender: v.patientGender,
        checkInAt: v.checkInAt,
        diagnosis: agg.note?.diagnosis ?? null,
        medicines: rx.medicines,
        showComposition: printCompositionPref(),
        reviewNeeded: v.reviewNeeded,
        reviewDate: v.reviewDate,
        doctorName: v.doctorName,
        doctorQualifications: v.doctorQualifications,
        doctorRegNumber: v.doctorRegNumber,
      });
      printPdf(doc);
    } catch {
      toast.error("Failed to print prescription");
    } finally {
      setPrintingVisitId(null);
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 dark:bg-dark-700">
              <TableHead>Date</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Medicines</TableHead>
              <TableHead className="w-28 text-right">Print</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visits.slice(0, 20).map((v) => (
              <TableRow
                key={v.id}
                className="cursor-pointer even:bg-cream-50/50"
                onClick={() => canViewEmr && navigate(`/emr/visits/${v.id}`)}
                onMouseEnter={() => setExpanded(v.id)}
              >
                <TableCell className="text-sm">{fmtDate(v.checkInAt)}</TableCell>
                <TableCell className="text-sm">{v.doctorName ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted">
                  {expanded === v.id && prescriptions.length > 0
                    ? `${prescriptions[0].medicines.length} drug(s)`
                    : "Click to view"}
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {canViewEmr && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      disabled={printingVisitId !== null}
                      onClick={() => void printPrescription(v.id)}
                      title="Print this prescription"
                    >
                      {printingVisitId === v.id ? (
                        <LoaderCircle size={13} className="mr-1 animate-spin" />
                      ) : (
                        <Printer size={13} className="mr-1" />
                      )}
                      Print
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PatientLabsTab({ patientId }: { patientId: string }) {
  const { data: visitsData, isLoading } = usePatientVisits(patientId);
  const navigate = useNavigate();
  const { isAdmin, permissions } = useAuth();
  const canViewEmr = isAdmin || permissions.has("emr.view");
  const visits = visitsData?.rows ?? [];

  if (isLoading) return <Skeleton className="h-32" />;
  if (visits.length === 0) return <EmptyState icon={Activity} label="No lab orders" />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 dark:bg-dark-700">
              <TableHead>Visit date</TableHead>
              <TableHead>Doctor</TableHead>
              <TableHead>Diagnosis</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visits.map((v) => (
              <TableRow
                key={v.id}
                className="cursor-pointer even:bg-cream-50/50"
                onClick={() => canViewEmr && navigate(`/emr/visits/${v.id}`)}
              >
                <TableCell className="text-sm">{fmtDate(v.checkInAt)}</TableCell>
                <TableCell className="text-sm">{v.doctorName ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted">{v.diagnosis ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function PatientInvoicesTab({ patientId }: { patientId: string }) {
  const { data, isLoading } = useInvoices(patientId);
  const navigate = useNavigate();
  const rows = data?.rows ?? [];

  if (isLoading) return <Skeleton className="h-32" />;
  if (rows.length === 0) return <EmptyState icon={FileText} label="No invoices yet" />;

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 dark:bg-dark-700">
              <TableHead>Invoice #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((inv) => (
              <TableRow
                key={inv.id}
                className="cursor-pointer even:bg-cream-50/50"
                onClick={() => navigate(`/billing/${inv.id}`)}
              >
                <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                <TableCell className="text-sm">{fmtDate(inv.date)}</TableCell>
                <TableCell className="text-right text-sm">{fmtINR(inv.totalPaise)}</TableCell>
                <TableCell><InvoiceStatusBadge status={inv.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function DocumentViewerInline({ patientId, onOpenExternal }: { patientId: string; onOpenExternal: () => void }) {
  const { data, isLoading } = useDocuments(patientId);
  const rows = data?.rows ?? [];

  if (isLoading) return <Skeleton className="h-32" />;
  if (rows.length === 0) return <EmptyState icon={FileText} label="No documents yet" />;

  return (
    <div className="space-y-2">
      {rows.slice(0, 10).map((doc) => (
        <div key={doc.id} className="flex items-center gap-2 rounded-md border border-cream-200 p-2 text-sm dark:border-dark-700">
          <FileText size={14} className="shrink-0 text-maroon-600" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-ink dark:text-cream-50">{doc.fileName}</div>
            <div className="text-xs text-muted">{doc.type ?? "Document"} · {fmtDate(doc.documentDate)}</div>
          </div>
        </div>
      ))}
      {rows.length > 10 && (
        <Button variant="link" size="sm" onClick={onOpenExternal}>View all ({rows.length})</Button>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-cream-200 p-8 text-center dark:border-dark-700">
      <Icon size={32} className="mb-2 text-muted" />
      <div className="text-sm text-muted">{label}</div>
    </div>
  );
}
